import './account.css';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, errorText } from '../../api';
import { t, tn } from '../../i18n';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { Loading } from '../belege/Pages';
import { usePage } from '../page';

interface LoginCodeState {
  available: boolean;
  required: boolean;
  enabled: boolean;
  email: string;
}

const RESEND_SECONDS = 30;

// Login-Code per E-Mail ein- oder ausschalten (Menü oben rechts). Beides bestätigt ein Code an die Login-E-Mail.
export function LoginCodePage() {
  usePage({
    title: t('Login-Code'),
    back: '/app',
    nav: 'start',
    crumbs: [{ label: t('Übersicht'), to: '/app' }, { label: t('Login-Code') }],
  });
  const res = useApi<LoginCodeState>('/api/auth/login-code');
  const d = res.data;
  // eingeschaltet bzw. ausgeschaltet werden soll: Code ist unterwegs
  const [pending, setPending] = useState<boolean | null>(null);
  const toast = useToast();

  return (
    <>
      <div className="hello">
        <h2>{t('Login-Code per E-Mail')}</h2>
        <p>{t('Mehr Schutz für Ihre Daten: Nach dem Passwort kommt bei jeder Anmeldung ein Code per E-Mail.')}</p>
      </div>
      {!d ? (
        <Loading error={res.error} />
      ) : !d.available ? (
        <div className="card lc-card">
          <p className="lc-st">{t('Der Login-Code per E-Mail ist noch nicht verfügbar. Ihre Kanzlei richtet ihn gerade ein.')}</p>
        </div>
      ) : pending !== null ? (
        <ConfirmForm
          on={pending}
          email={d.email}
          onDone={async () => {
            setPending(null);
            await res.reload();
            toast(pending ? t('Login-Code ist an') : t('Login-Code ist aus'));
          }}
          onCancel={() => setPending(null)}
        />
      ) : (
        <StartCard d={d} onSent={setPending} />
      )}
    </>
  );
}

function StartCard({ d, onSent }: { d: LoginCodeState; onSent(on: boolean): void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const on = !d.enabled;

  async function start() {
    setError(null);
    setBusy(true);
    try {
      await api.post('/api/auth/login-code/start', { on });
      onSent(on);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <div className="card lc-card">
      <p className="lc-st">
        {d.enabled ? (
          <>
            <span className="badge b-ok">{t('an')}</span>{' '}
            {tn('Bei jeder Anmeldung schicken wir einen Code an {email}.', { email: <b>{d.email}</b> })}
          </>
        ) : (
          <>
            <span className="badge b-none">{t('aus')}</span> {t('Sie melden sich nur mit E-Mail und Passwort an.')}
          </>
        )}
      </p>
      {!d.required && (
        <>
          {error && (
            <p className="formerr" role="alert">
              {error}
            </p>
          )}
          <div className="row">
            <button type="button" className={`btn2${on ? ' dark' : ''}`} disabled={busy} onClick={start}>
              {busy ? t('Einen Moment …') : on ? t('Login-Code einschalten') : t('Login-Code ausschalten')}
            </button>
          </div>
          <div className="src">
            {on
              ? t(
                  'Zum Einschalten schicken wir Ihnen einen Code an {email}. Andere angemeldete Geräte melden sich danach einmal neu an.',
                  { email: d.email },
                )
              : t('Zum Ausschalten schicken wir Ihnen einen Code an {email}.', { email: d.email })}
          </div>
        </>
      )}
    </div>
  );
}

function ConfirmForm({ on, email, onDone, onCancel }: { on: boolean; email: string; onDone(): Promise<void>; onCancel(): void }) {
  const toast = useToast();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setTimeout(() => setWait((w) => w - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [wait]);

  function failed(err: unknown) {
    // Code abgelaufen oder zu oft falsch: zurück, neu anfordern
    if (err instanceof ApiError && ['code_missing', 'code_expired', 'code_locked', 'too_many'].includes(err.code ?? '')) {
      toast(errorText(err));
      onCancel();
      return;
    }
    setInfo(null);
    setError(errorText(err));
    document.getElementById('lc-code')?.focus();
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError(t('Bitte den 6-stelligen Code aus der E-Mail eingeben.'));
      document.getElementById('lc-code')?.focus();
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/login-code/confirm', { on, code });
      await onDone();
    } catch (err) {
      failed(err);
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    try {
      await api.post('/api/auth/login-code/resend', { on });
      setCode('');
      setInfo(t('Neuer Code an {email} verschickt.', { email }));
      setWait(RESEND_SECONDS);
    } catch (err) {
      failed(err);
    }
  }

  return (
    <form className="card lc-card form" onSubmit={submit} noValidate>
      <p className="lc-st">
        {on
          ? tn('Wir haben einen Code an {email} geschickt. Bitte geben Sie ihn ein, um den Login-Code einzuschalten.', {
              email: <b>{email}</b>,
            })
          : tn('Wir haben einen Code an {email} geschickt. Bitte geben Sie ihn ein, um den Login-Code auszuschalten.', {
              email: <b>{email}</b>,
            })}
      </p>
      <div className="fld">
        <label htmlFor="lc-code">
          {t('Code aus der E-Mail')}
          <input
            id="lc-code"
            className="code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'lc-err' : undefined}
          />
        </label>
      </div>
      {error && (
        <p className="formerr" role="alert" id="lc-err">
          {error}
        </p>
      )}
      {info && (
        <p className="lc-info" role="status">
          {info}
        </p>
      )}
      <div className="row">
        <button className="btn2 dark" type="submit" disabled={busy}>
          {busy ? t('Einen Moment …') : on ? t('Einschalten') : t('Ausschalten')}
        </button>
        <button className="btn2" type="button" onClick={onCancel}>
          {t('Abbrechen')}
        </button>
      </div>
      <div className="src">
        {t('Der Code gilt 10 Minuten. Keine E-Mail da? Bitte auch im Spam-Ordner nachsehen.')}{' '}
        <button type="button" className="linkbtn" disabled={wait > 0} onClick={resend}>
          {wait > 0 ? t('Code erneut senden (in {n} s)', { n: wait }) : t('Code erneut senden')}
        </button>
      </div>
    </form>
  );
}
